import { useEffect, useRef, useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TIMEFRAME_LABELS, TIMEFRAME_OPTIONS } from './catalog'

export interface WorkbenchTimeframeProps {
  value: string
  onChange: (tf: string) => void
}

/**
 * Timeframe switcher for the chart top toolbar. Custom dropdown (not Radix) in
 * the toolbar's button grammar. Shows the TV-friendly label (1m/1h/1D) since
 * this surface emulates TradingView; `value`/`onChange` carry the engine id.
 */
export function WorkbenchTimeframe({ value, onChange }: WorkbenchTimeframeProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const label = (TIMEFRAME_LABELS as Record<string, string>)[value] ?? value

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (rootRef.current && e.target instanceof Node && !rootRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open])

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-label tabular-nums transition-colors cursor-pointer',
          'text-foreground hover:bg-muted/60',
          open && 'bg-muted',
        )}
      >
        {label}
        <ChevronDown
          size={12}
          strokeWidth={2}
          className={cn('text-muted-foreground transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-20 w-24 rounded-md border border-border bg-popover shadow-popover p-1">
          {TIMEFRAME_OPTIONS.map((tf) => {
            const on = tf === value
            return (
              <button
                key={tf}
                type="button"
                onClick={() => {
                  onChange(tf)
                  setOpen(false)
                }}
                className={cn(
                  'w-full flex items-center gap-2 text-left text-body px-2 py-1.5 rounded-md tabular-nums transition-colors cursor-pointer',
                  on ? 'text-foreground' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                )}
              >
                <span className="inline-flex h-3.5 w-3.5 items-center justify-center">
                  {on && <Check size={13} strokeWidth={2.25} className="text-foreground" />}
                </span>
                {TIMEFRAME_LABELS[tf]}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
